import React, { FC, Suspense, useEffect, useState } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { lazy } from '@loadable/component';
import { AuthenticatedRoute } from '@fluentsms/auth';
import useScrollReset from './useScrollReset';
import { PageLoadingIndicator } from '@fluentsms/components';

const ProtectedRoutes = lazy(() => import(/* webpackPrefetch: true */ './ProtectedRoutes'));
const Login = lazy(() => import('./Components/Login'));
const ForgotPassword = lazy(() => import('./Components/ForgotPassword'));
const Logout = lazy(() => import('./Components/Logout'));

interface VersionFileJson {
  version: string;
}
const RELEASE_VERSION_PATH = '/release-version.json';

const Routes: FC = () => {
  const {
    location: { pathname },
  } = useHistory();
  const [initialAppVersion, setInitialAppVersion] = useState<string | undefined | null>();

  useEffect(() => {
    const hasAttemptedInit = initialAppVersion !== undefined;
    const isSuccessfulInit = hasAttemptedInit && initialAppVersion !== null;

    // No point in fetching if we've failed to receive it before
    if (hasAttemptedInit && !isSuccessfulInit) {
      return;
    }

    fetch(RELEASE_VERSION_PATH, {
      method: 'GET',
      cache: 'no-store',
    }).then(async (response) => {
      /**
       * See README.md
       *
       * For additional testing, uncomment the test lines for the `/titles` pathname below.
       * TODO: Better way to capture this?
       *
       * @see VersionFileJson
       */
      let newAppVersionJson: VersionFileJson | undefined = undefined;
      try {
        newAppVersionJson = await response.json();
        // Keeping commented out as a verified way to test this (as of 9/13/23).
        // if (window.location.pathname.includes('/titles')) {
        //   newAppVersionJson = { version: 'v1.LOCALHOST.TITLE' };
        // }
      } catch (e) {
        console.error(e);
      }

      if (isSuccessfulInit) {
        if (!newAppVersionJson) {
          console.error('Unexpected error: App Version reloading disabled - No version detected.');
        } else if (initialAppVersion !== newAppVersionJson?.version) {
          window.location.reload();
        }
      } else if (!hasAttemptedInit) {
        if (newAppVersionJson) {
          console.info(`Initial App Version: ${newAppVersionJson.version}`);
        } else if (window.location.hostname?.includes('localhost')) {
          console.info('To enable app versioning testing in localhost, please configure release-version.json');
        } else {
          console.info('App versioning disabled; unable to find release version reference');
        }

        setInitialAppVersion(newAppVersionJson?.version ?? null);
      }
    });
  }, [pathname, initialAppVersion]);

  useScrollReset();
  return (
    <Suspense fallback={<PageLoadingIndicator />}>
      <Switch>
        <Route path="/login" exact component={Login} />
        <Route path="/logout" exact component={Logout} />
        <Route path="/forgot-password" exact component={ForgotPassword} />
        <AuthenticatedRoute path="/" component={ProtectedRoutes} />
      </Switch>
    </Suspense>
  );
};

export default Routes;
